import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  getOrgResponses,
  delegateResponse,
  getOrgTeamMembers,
  getResponseById,
  QuestionnaireResponse
} from "../../../utils/api/endpoints/questionnaire";
import { IntakeFormComponent } from "../../scoping/IntakeFormComponent";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import toast from "react-hot-toast";
import {
  UserPlus,
  FileText,
  AlertCircle,
  Shield,
  Layers,
  Clock,
  ChevronDown,
  X,
  Eye
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────────────
   HistoryOfScopes
   Displays all scoping sessions for the org and lets owners/admins grant
   read-only view access (delegation) to any team member so they can review
   the submitted questionnaire in the form UI (read mode only — they cannot
   edit or re-submit).
──────────────────────────────────────────────────────────────────────────── */
const getTargetCount = (answers: any): number => {
  if (!answers) return 0;
  try {
    const parsed = typeof answers === "string" ? JSON.parse(answers) : answers;
    const assets = parsed.business_assets || [];
    let count = 0;
    assets.forEach((asset: any) => {
      asset.assessment_scopes?.forEach((s: any) => {
        count += (s.targets || []).length;
      });
    });
    return count;
  } catch (e) {
    return 0;
  }
};

const HistoryOfScopes: React.FC = () => {
  const { role, id: currentUserId } = useUserCredentials();
  const [scopes, setScopes] = useState<QuestionnaireResponse[]>([]);
  const [teamMembers, setTeamMembers] = useState<
    { user_id: number; username: string; email: string; display_name?: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSelectScopeId, setActiveSelectScopeId] = useState<string | null>(null);
  const [viewingScope, setViewingScope] = useState<any | null>(null);
  const [viewingData, setViewingData] = useState<any | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const filteredScopes = useMemo(() => {
    return scopes.filter((scope) => {
      if (statusFilter !== "ALL" && scope.status !== statusFilter) {
        return false;
      }
      if (searchQuery.trim() !== "") {
        const query = searchQuery.toLowerCase();
        let targetMatch = false;
        try {
          const parsed = typeof scope.answers === "string" ? JSON.parse(scope.answers) : scope.answers;
          const assets = parsed?.business_assets || [];
          assets.forEach((asset: any) => {
            asset.assessment_scopes?.forEach((s: any) => {
              s.targets?.forEach((t: any) => {
                if (t.target_value?.toLowerCase().includes(query)) {
                  targetMatch = true;
                }
              });
            });
          });
        } catch (e) {}

        const clientNameMatch = scope.client_name?.toLowerCase().includes(query);
        const clientEmailMatch = scope.client_email?.toLowerCase().includes(query);
        const statusMatch = scope.status?.toLowerCase().includes(query);

        return targetMatch || clientNameMatch || clientEmailMatch || statusMatch;
      }
      return true;
    });
  }, [scopes, statusFilter, searchQuery]);

  const canDelegate =
    role === UserRole.BUSINESS || role === UserRole.BUSINESS_ADMINISTRATOR;

  const isOwner = role === UserRole.BUSINESS;

  /* ── Load data ─────────────────────────────────────────────── */
  const fetchData = async () => {
    try {
      setLoading(true);
      const [responses, members] = await Promise.all([
        getOrgResponses(),
        canDelegate ? getOrgTeamMembers() : Promise.resolve([])
      ]);
      setScopes(responses);
      const filteredMembers = members.filter(m => m.user_id !== currentUserId);
      setTeamMembers(filteredMembers);
    } catch (err: any) {
      console.error("Failed to load scoping history:", err);
      toast.error("Failed to load scoping history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleViewDetails = async (scope: any) => {
    if (scope.status === "DRAFT") {
      window.open(`/intake/${scope.share_token}`, "_blank");
      return;
    }
    setLoadingDetail(true);
    const toastId = toast.loading("Loading secure details...");
    try {
      const detail = await getResponseById(scope.id);
      const initialData = {
        id: detail.id,
        client_name: detail.client_name,
        client_email: detail.client_email,
        status: detail.status,
        schema_snapshot: detail.schema_snapshot,
        answers: detail.answers,
        readOnly: true,
        submitted_at: detail.submitted_at,
        showSubmittedAnswers: true
      };
      setViewingData(initialData);
      setViewingScope(detail);
      toast.dismiss(toastId);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load secure scope details",
        { id: toastId }
      );
    } finally {
      setLoadingDetail(false);
    }
  };

  /* ── Multi-Delegate handler ────────────────────────────────── */
  const handleDelegateList = async (scopeId: string, updatedIds: number[], actionType: "grant" | "revoke", name?: string) => {
    setSaving(true);
    const toastId = toast.loading("Updating access...");
    try {
      await delegateResponse(scopeId, updatedIds);
      toast.success(
        actionType === "grant" ? `View access granted to ${name}` : `View access revoked for ${name}`,
        { id: toastId }
      );
      fetchData();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to update access",
        { id: toastId }
      );
    } finally {
      setSaving(false);
    }
  };

  /* ── Counters ──────────────────────────────────────────────── */
  const totalCount     = scopes.length;
  const draftCount     = scopes.filter(s => s.status === "DRAFT").length;
  const submittedCount = scopes.filter(s => s.status === "SUBMITTED").length;
  const convertedCount = scopes.filter(s => s.status === "CONVERTED").length;

  /* ── Status pill ────────────────────────────────────────────── */
  const StatusPill = ({
    status,
    isExpired
  }: {
    status: string;
    isExpired: boolean;
  }) => {
    if (status === "CONVERTED")
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/70 whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
          Active Program
        </span>
      );
    if (status === "REJECTED")
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200/70 whitespace-nowrap">
          Rejected
        </span>
      );
    if (status === "SUBMITTED")
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200/70 whitespace-nowrap">
          Under Review
        </span>
      );
    if (isExpired)
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-400 border border-slate-200/70 line-through whitespace-nowrap">
          Expired
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200/70 whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
        Draft
      </span>
    );
  };

  /* ── Date helper ─────────────────────────────────────────────── */
  const fmtDate = (d?: string | null) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric"
        })
      : null;

  /* ── Member name helper ─────────────────────────────────────── */
  const memberName = (m: (typeof teamMembers)[0]) =>
    m.display_name || m.username || m.email;

  /* ── Delegation cell ─────────────────────────────────────────
     Delegation = granting a team member read-only view of the
     submitted questionnaire in the form UI.
     Available for: SUBMITTED and CONVERTED sessions.
     Not available for: DRAFT (nothing to view yet) or REJECTED.
   ─────────────────────────────────────────────────────────────── */
  const DelegationCell = ({ scope }: { scope: any }) => {
    let currentDelegateIds: number[] = [];
    try {
      const parsed = scope.delegated_user_ids ? JSON.parse(scope.delegated_user_ids) : [];
      currentDelegateIds = Array.isArray(parsed) ? parsed.map((id: any) => Number(id)) : [];
    } catch (e) {
      currentDelegateIds = [];
    }

    const currentDelegates = teamMembers.filter(m => currentDelegateIds.includes(m.user_id));
    const remainingMembers = teamMembers.filter(m => !currentDelegateIds.includes(m.user_id));

    /* DRAFT — nothing submitted yet, no point delegating */
    if (scope.status === "DRAFT") {
      return (
        <span className="text-[11px] text-slate-400 italic">
          Submit first to share read access
        </span>
      );
    }



    return (
      <div className="flex flex-wrap items-center gap-1.5 max-w-xs md:max-w-md">
        {/* Render existing delegates as pills */}
        {currentDelegates.map(m => (
          <span
            key={m.user_id}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-indigo-50 border border-indigo-100 text-indigo-700 text-[11px] font-semibold whitespace-nowrap"
          >
            <Eye className="w-3 h-3 text-indigo-400 shrink-0" />
            <span className="truncate max-w-[120px]">{memberName(m)}</span>
            {canDelegate && (
              <button
                disabled={saving}
                onClick={() => {
                  const updated = currentDelegateIds.filter(id => id !== m.user_id);
                  handleDelegateList(scope.id, updated, "revoke", memberName(m));
                }}
                className="p-0.5 rounded-full hover:bg-indigo-100 text-indigo-400 hover:text-indigo-600 transition-colors cursor-pointer disabled:opacity-50 shrink-0"
                title="Revoke access"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}

        {/* If no delegates and can't delegate */}
        {!canDelegate && currentDelegates.length === 0 && (
          <span className="text-slate-355">—</span>
        )}

        {/* Dropdown to add a delegate */}
        {canDelegate && remainingMembers.length > 0 && (
          <div className="relative shrink-0">
            <button
              disabled={saving}
              onClick={(e) => {
                e.stopPropagation();
                setActiveSelectScopeId(activeSelectScopeId === scope.id ? null : scope.id);
              }}
              className="px-2.5 py-1 text-[11px] border border-slate-200 hover:border-indigo-200 hover:bg-slate-50 rounded bg-white font-semibold text-slate-500 hover:text-indigo-600 shadow-sm flex items-center gap-1 transition-all cursor-pointer disabled:opacity-60"
            >
              <span>+ Add view access</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>

            {activeSelectScopeId === scope.id && (
              <>
                {/* Backdrop to close when clicking outside */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setActiveSelectScopeId(null)}
                />
                <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-20 max-h-48 overflow-y-auto">
                  {remainingMembers.map(m => (
                    <button
                      key={m.user_id}
                      onClick={() => {
                        handleDelegateList(scope.id, [...currentDelegateIds, m.user_id], "grant", memberName(m));
                        setActiveSelectScopeId(null);
                      }}
                      className="w-full text-left px-3 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                    >
                      {memberName(m)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  /* ── Render ──────────────────────────────────────────────────── */
  if (viewingScope && viewingData) {
    return (
      <div className="space-y-6" style={{ fontFamily: "Montserrat, sans-serif" }}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setViewingScope(null);
                setViewingData(null);
              }}
              className="px-3 py-1.5 text-xs font-semibold text-slate-655 hover:text-slate-900 border border-slate-200 hover:border-slate-350 bg-white rounded-lg transition-all cursor-pointer"
            >
              &larr; Back to History
            </button>
            <div>
              <h2 className="text-lg font-bold text-slate-900 leading-tight">
                {viewingScope.client_name || "Scoping Session"}
              </h2>
              <p className="text-xs text-slate-450 font-normal mt-0.5">
                {viewingScope.client_email || "No email provided"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-slate-400 font-semibold">
              Submitted: {fmtDate(viewingScope.submitted_at) || "Pending"}
            </span>
            <StatusPill status={viewingScope.status} isExpired={false} />
          </div>
        </div>

        {/* Secure Form Viewer */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <IntakeFormComponent
            token={viewingScope.share_token}
            initialData={viewingData}
            isDark={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: "Montserrat, sans-serif" }}>

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-slate-100">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            History of Scopes
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Track scoping assessments for your organization. Grant team members
            read-only view access to submitted questionnaires.
          </p>
        </div>
        <div className="text-xs text-slate-400 font-medium shrink-0">
          {totalCount} session{totalCount !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total",     value: totalCount,     icon: <FileText    className="w-4 h-4 text-slate-500"   />, accent: "border-slate-200/80",   val: "text-slate-800"  },
          { label: "Drafts",    value: draftCount,     icon: <Layers      className="w-4 h-4 text-amber-500"   />, accent: "border-amber-200/70",   val: "text-amber-700"  },
          { label: "Submitted", value: submittedCount, icon: <AlertCircle className="w-4 h-4 text-blue-500"    />, accent: "border-blue-200/70",    val: "text-blue-700"   },
          { label: "Converted", value: convertedCount, icon: <Shield      className="w-4 h-4 text-emerald-500" />, accent: "border-emerald-200/70", val: "text-emerald-700" }
        ].map((stat, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 bg-white rounded-xl border ${stat.accent} px-4 py-3 shadow-sm`}
          >
            <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 shrink-0">
              {stat.icon}
            </div>
            <div className="min-w-0">
              <div className={`text-xl font-extrabold ${stat.val} leading-none`}>
                {stat.value}
              </div>
              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide mt-0.5">
                {stat.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-white border border-slate-200/85 rounded-xl p-4 shadow-sm">
        {/* Search Input */}
        <div className="relative w-full sm:w-72">
          <input
            type="text"
            placeholder="Search targets or client info..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 placeholder-slate-400 text-slate-800"
          />
          <div className="absolute left-3 top-2.5 text-slate-400 pointer-events-none">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
        </div>

        {/* Status Pills */}
        <div className="flex flex-wrap gap-1.5 items-center bg-slate-50 border border-slate-100 rounded-lg p-1">
          {[
            { value: "ALL", label: "All" },
            isOwner && { value: "DRAFT", label: "Drafts" },
            { value: "SUBMITTED", label: "Under Review" },
            { value: "CONVERTED", label: "Active Program" }
          ].filter((p): p is any => !!p).map((pill) => {
            const active = statusFilter === pill.value;
            return (
              <button
                key={pill.value}
                onClick={() => setStatusFilter(pill.value)}
                className={`px-3 py-1.5 rounded-md text-[11px] font-bold transition-all duration-150 cursor-pointer ${
                  active
                    ? "bg-[#010D2C] text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-850 hover:bg-slate-200/40"
                }`}
              >
                {pill.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">

        {loading ? (
          <div className="py-16 flex flex-col items-center gap-3">
            <div className="w-7 h-7 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-slate-400 font-medium">Loading scoping history…</p>
          </div>

        ) : scopes.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-3 text-center px-8">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <FileText className="w-7 h-7 text-slate-300" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-700">No scoping sessions yet</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Sessions generated by our team will appear here once active.
              </p>
            </div>
          </div>

        ) : filteredScopes.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-3 text-center px-8">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <svg className="w-7 h-7 text-slate-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-700">No matching scopes found</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Try adjusting your search query or status filters.
              </p>
            </div>
          </div>

        ) : (
          <div className={`overflow-x-auto ${activeSelectScopeId ? "pb-36" : ""}`}>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {[
                    { label: "S.No.",     cls: `px-4 py-3 text-left  ${isOwner ? "w-[10%]" : "w-[15%]"}` },
                    { label: "Targets",    cls: `px-3 py-3 text-left  ${isOwner ? "w-[18%]" : "w-[30%]"}` },
                    { label: "Created",   cls: `px-3 py-3 text-left  ${isOwner ? "w-[13%]" : "w-[18%]"}` },
                    { label: "Submitted", cls: `px-3 py-3 text-left  ${isOwner ? "w-[13%]" : "w-[18%]"}` },
                    { label: "Status",    cls: `px-3 py-3 text-center ${isOwner ? "w-[14%]" : "w-[19%]"}` },
                    isOwner && {
                      label: "Read-only Access",
                      cls: "px-4 py-3 text-left  w-[32%]",
                      tooltip: "Grant a team member view-only access to the submitted questionnaire"
                    }
                  ].filter((c): c is any => !!c).map((col) => (
                    <th
                      key={col.label}
                      title={col.tooltip}
                      className={`${col.cls} text-[10px] font-extrabold uppercase tracking-wider text-slate-400`}
                    >
                      {col.label === "Read-only Access" ? (
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3 text-slate-300" />
                          {col.label}
                        </span>
                      ) : col.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100/80">
                {filteredScopes.map((scope: any, idx: number) => {
                  const isExpired =
                    scope.expires_at && new Date(scope.expires_at) < new Date();
                  return (
                    <tr
                      key={scope.id}
                      className="hover:bg-slate-50/50 transition-colors duration-150"
                    >
                      {/* S.No. */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <button
                            disabled={loadingDetail}
                            onClick={() => handleViewDetails(scope)}
                            className="p-2 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100/80 text-indigo-500 rounded-lg shrink-0 transition-colors cursor-pointer"
                            title="View questionnaire answers securely"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <span className="font-semibold text-slate-800 text-[13px] leading-tight">
                            {idx + 1}
                          </span>
                        </div>
                      </td>

                      {/* Targets */}
                      <td className="px-3 py-3">
                        <span className="text-[12px] font-semibold text-slate-700">
                          {getTargetCount(scope.answers)} target{getTargetCount(scope.answers) !== 1 ? "s" : ""}
                        </span>
                      </td>

                      {/* Created */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <Clock className="w-3 h-3 text-slate-300 shrink-0" />
                          <span className="text-[12px] font-medium whitespace-nowrap">
                            {fmtDate(scope.created_at) ?? "—"}
                          </span>
                        </div>
                      </td>

                      {/* Submitted */}
                      <td className="px-3 py-3">
                        <span className="text-[12px] font-medium text-slate-500 whitespace-nowrap">
                          {fmtDate(scope.submitted_at) ?? (
                            <span className="text-slate-300 italic">Pending</span>
                          )}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3 text-center">
                        <StatusPill status={scope.status} isExpired={!!isExpired} />
                      </td>

                      {/* Read-only access / delegation */}
                      {isOwner && (
                        <td className="px-4 py-3">
                          <DelegationCell scope={scope} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend — explains what "read-only access" means */}
      {isOwner && canDelegate && scopes.some(s => s.status !== "DRAFT" && s.status !== "REJECTED") && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
          <Eye className="w-3 h-3 shrink-0" />
          <span>
            <strong className="text-slate-500">Read-only Access</strong> lets a team member
            view the submitted questionnaire answers in the form UI — they cannot edit or
            re-submit.
          </span>
        </p>
      )}
    </div>
  );
};

export default HistoryOfScopes;
